import { useEffect, useRef, useState } from 'react';
import { Check, SmilePlus, Volume2, VolumeX, Paperclip, X } from 'lucide-react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import PageFooter from '../../../components/PageFooter/PageFooter';
import SlackText from '../../../components/SlackText/SlackText';
import { useAnnouncements } from '../../../context/AnnouncementsProvider';
import { REACTION_EMOJIS } from '../../../services/announcements';
import { isChimeMuted, setChimeMuted, playAnnouncementChime } from '../../../utils/announcementChime';
import { lockScroll, unlockScroll } from '../../../utils/scrollLock';
import styles from './Announcements.module.css';

/* AP-style month abbreviations — the short months keep their full name, which
   is why this isn't a slice of a locale-formatted date. */
const MONTHS = ['Jan.', 'Feb.', 'March', 'April', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];

/* Every getter here is the local-time one, so the stamp is already in the
   reader's own timezone whatever the api sent. */
function formatSentAt(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} · ${time}`;
}

const isImage = file => (file.mimeType || '').startsWith('image/');

function Announcements() {
  usePageTitle('Announcements');
  const { announcements, loading, error, markAllRead, react } = useAnnouncements();
  const [pickerFor, setPickerFor] = useState(null);
  const [muted, setMuted] = useState(isChimeMuted);
  const [lightbox, setLightbox] = useState(null);
  const feedRef = useRef(null);

  /* Opening the channel is what marks it read — the bell's dot clears as soon
     as the messages are on screen, not when the last one scrolls into view. */
  useEffect(() => {
    markAllRead();
  }, [markAllRead]);

  /* Newest message sits at the bottom, so land there the way a chat app does. */
  useEffect(() => {
    if (!loading && feedRef.current) {
      feedRef.current.scrollIntoView({ block: 'end' });
    }
  }, [loading]);

  useEffect(() => {
    if (!lightbox) return;
    lockScroll();
    const onKey = e => { if (e.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockScroll();
    };
  }, [lightbox]);

  useEffect(() => {
    if (!pickerFor) return;
    const close = e => {
      if (!e.target.closest(`.${styles.actions}`)) setPickerFor(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [pickerFor]);

  function toggleSound() {
    const next = !muted;
    setMuted(next);
    setChimeMuted(next);
    /* Unmuting plays the chime once, both as a preview and because this click
       is what lets the browser start an audio context at all. */
    if (!next) playAnnouncementChime();
  }

  function pick(id, emoji) {
    setPickerFor(null);
    react(id, emoji);
  }

  return (
    <DashboardLayout>
      <div className={styles.page}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.channelName}>
              POW
              {/* A filled disc with a tick, not lucide's scalloped BadgeCheck —
                  the badge is drawn from a plain Check on a styled circle so the
                  fill and the dark-mode ring are ours to set. */}
              <span className={styles.verified} role="img" aria-label="Verified channel">
                <Check size={13} strokeWidth={3.5} />
              </span>
            </h1>
            <p className={styles.channelTagline}>Progress overcome win</p>
          </div>

          {/* <button
            type="button"
            className={styles.soundBtn}
            onClick={toggleSound}
            aria-pressed={!muted}
            title={muted ? 'Turn announcement sound on' : 'Turn announcement sound off'}
            aria-label={muted ? 'Turn announcement sound on' : 'Turn announcement sound off'}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button> */}
        </header>

        <div className={styles.feed} ref={feedRef}>
          {loading && <p className={styles.notice}>Loading announcements…</p>}
          {!loading && error && <p className={styles.notice}>{error}</p>}
          {!loading && !error && announcements.length === 0 && (
            <p className={styles.notice}>No announcements yet. New ones will appear here.</p>
          )}

          {announcements.map(announcement => {
            /* A file whose signed URL couldn't be minted is dropped rather than
               rendered as a broken image. */
            const usable = (announcement.attachments || []).filter(file => file.url);
            const images = usable.filter(isImage);
            const files  = usable.filter(file => !isImage(file));

            return (
            <article key={announcement.id} className={styles.row}>
              <div className={styles.bubbleWrap}>
                <div className={styles.bubble}>
                  {(images.length > 0 || files.length > 0) && (
                    <div className={styles.attachments}>
                      {images.length > 0 && (
                        <div
                          className={styles.imageGrid}
                          /* One image gets the full 360px square; beyond that the
                             grid tightens to three across and the squares shrink
                             to fit the same block width. */
                          style={{ '--an-cols': Math.min(images.length, 3) }}
                        >
                          {images.map((file, i) => (
                            <button
                              key={file.url || i}
                              type="button"
                              className={styles.imageLink}
                              onClick={() => setLightbox(file)}
                            >
                              <img
                                src={file.url}
                                alt={file.name || 'Announcement attachment'}
                                loading="lazy"
                              />
                            </button>
                          ))}
                        </div>
                      )}

                      {files.map((file, i) => (
                        <a
                          key={file.url || i}
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.fileChip}
                        >
                          <Paperclip size={15} />
                          {file.name || 'Attachment'}
                        </a>
                      ))}
                    </div>
                  )}

                  {announcement.text && (
                    <SlackText text={announcement.text} className={styles.text} />
                  )}

                  {announcement.reactions.length > 0 && (
                    <div className={styles.reactionList}>
                      {announcement.reactions.map(({ emoji, count }) => (
                        <button
                          key={emoji}
                          type="button"
                          className={`${styles.reactionPill} ${
                            announcement.myReactions.includes(emoji) ? styles.reactionPillMine : ''
                          }`}
                          onClick={() => react(announcement.id, emoji)}
                          aria-pressed={announcement.myReactions.includes(emoji)}
                        >
                          <span className={styles.reactionEmoji}>{emoji}</span>
                          {count}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.actions}>
                  {pickerFor === announcement.id && (
                    <div className={styles.picker} role="group" aria-label="React to this announcement">
                      {REACTION_EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          className={styles.pickerBtn}
                          onClick={() => pick(announcement.id, emoji)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className={styles.addReactionBtn}
                    aria-label="Add a reaction"
                    aria-expanded={pickerFor === announcement.id}
                    onClick={() => setPickerFor(id => (id === announcement.id ? null : announcement.id))}
                  >
                    <SmilePlus size={18} />
                  </button>
                </div>
              </div>

              <time className={styles.sentAt} dateTime={announcement.publishedAt}>
                {formatSentAt(announcement.publishedAt)}
                {announcement.editedAt && <span className={styles.edited}> · edited</span>}
              </time>
            </article>
            );
          })}
        </div>

        <PageFooter />

        {lightbox && (
          <div
            className={styles.lightbox}
            role="dialog"
            aria-modal="true"
            aria-label={lightbox.name || 'Announcement image'}
            onClick={() => setLightbox(null)}
          >
            <button
              type="button"
              className={styles.lightboxClose}
              aria-label="Close image"
              onClick={() => setLightbox(null)}
            >
              <X size={20} />
            </button>
            {/* Clicking the picture itself shouldn't dismiss it — only the
                backdrop around it. */}
            <img
              src={lightbox.url}
              alt={lightbox.name || 'Announcement image'}
              className={styles.lightboxImage}
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Announcements;
